import numeral from 'numeral';
import { FORMAT_TYPES, TOAST_TYPES, EXCEPTION_KEY_CODES, NUMBER_KEY_CODES, LOAN_INPUTS } from '@constants';
import { toastNotification, dismissToast, clearToastQueue } from './toast';

/* These formatting helper methods exist in the backend. 
   Only necessary here for loan calculator. */

export function convertToNumber(value) {
    return numeral(value).value();
}

export function formatValue(value, format) {
    return numeral(value).format(format);
}

/* These helpers are frontend-specific */

export function isValidNumber(number) {
    // Careful here; parseInt('3asdf323l') will return 3 (still returns a number if string starts with one)
    // The + operator, on the other hand, only accepts valid number strings 
    return number !== null && !isNaN(+number);
}

export function formatInput(value, inputName) {
    const { numbers } = FORMAT_TYPES;

    switch (inputName) {
        case 'loanAmount':
            return formatValue(value, numbers.noDecimal);
        default:
            return value;
    }
}

export function validateInput(event, inputName) {
    const { keyCode, key } = event;
    const { value } = event.target;
    const { length } = value.toString();
    const valueAsNumber = parseInt(value);
    const validKeyCodes = _getValidKeys(inputName);

    _preventInvalidKeys(event, validKeyCodes, keyCode);

    // Valid exception keys can always be pressed - i.e. backspace, etc
    const isValidException = Object.values(EXCEPTION_KEY_CODES).includes(keyCode);
    const isValidNumber = NUMBER_KEY_CODES.includes(keyCode);
    const maxLengthReached = _isMaxLength(inputName, length);
    
    const { loanAmount, APR, loanTerm, monthlyPayment } = LOAN_INPUTS;

    let condition = false;

    switch(inputName) {
        case loanAmount:
            break;
        case APR: {
            // Only add numbers (not keys like 'Backspace') to nextValue
            const nextValue = isValidNumber ? value + key : value;
            condition = parseFloat(nextValue) > 50;

            if (condition) {
                const message = 'APR should not be greater than 50';
                const options = { autoClose: false };

                toastNotification(TOAST_TYPES.error, message, options);
            } else {
                dismissToast();
                clearToastQueue();
            }

            break;
        }
        case loanTerm:
            condition = valueAsNumber > 99;

            break;
        case monthlyPayment:
            // Restrict values after decimal to two places
            condition = value.includes('.') && value.split('.')[1].length >= 2;
           
            break;
        default:
            condition = true;
    }

    return _preventDefaultWhenTrue(event, (condition || maxLengthReached) && !isValidException);
}

function _isMaxLength(name, length) {
    switch(name){
        case 'loanAmount':
            return length >= 16;
        case 'APR':
            return length >= 5;
        case 'loanTerm':
            return length >= 2;
        case 'monthlyPayment':
            return length >= 8;
        default:
            return false;
    }
}

function _preventDefaultWhenTrue(event, condition) {
    if (condition) {
        event.preventDefault();

        return false;
    }
}

function _getValidKeys(inputName) {
    // Limit keys to numbers (included by default for input type number) and backspace, decimal, arrow keys
    const exceptionKeys = Object.values(EXCEPTION_KEY_CODES);
    const validKeyCodes = [...exceptionKeys, ...NUMBER_KEY_CODES];

    const { decimal: decimalCode } = EXCEPTION_KEY_CODES;

    // Decimals are not valid for loanTerm field
    if (inputName === 'loanTerm') {
        return validKeyCodes.filter(key => key !== decimalCode);
    }

    return validKeyCodes;
}

function _preventInvalidKeys(event, validKeyCodes, keyCode) {
    const { decimal } = EXCEPTION_KEY_CODES;
    const { value } = event.target;

    const invalidKey = !validKeyCodes.includes(keyCode);
    // Prevent decimals from being typed twice
    const doubleDecimal = value.includes('.') && keyCode === decimal;

    if (invalidKey || doubleDecimal) {
        event.preventDefault();
    }
}
